import { Add } from '@mui/icons-material';
import { Box, Grid } from '@mui/material';
import { useImperativeHandle, useEffect, forwardRef, useCallback, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import {
  createInvoiceNote,
  createNote,
  archiveInvoiceNote,
  archiveNote,
  getAllNotes,
  getAllNotesForInvoice,
  getAllNotesForPlacement,
  createPlacementNote,
  updatePlacementNote,
  archivePlacementNote,
  NoteDTO,
  updateInvoiceNote,
  updateNote,
  getNotesByClientIdAndClientContactId,
  getAllRecruiterNotes,
  unarchiveNote,
} from 'wisestep-api';
import { EmptyTeamsAndUsersIcon, WsButton, WsListingLoader, WsToggleButton } from 'wisestep-base';

import ConfirmationDialog from '../../features/clientDetails/components/ConfirmationDialog';
import AddEditNote from '../../features/clientDetails/components/notes/AddEditNote';
import Note from '../../features/clientDetails/components/notes/Note';
import { useDialog } from '../../hooks/useDialog';
import { failed, success } from '../../utils/NotificationMessages';
import { useNotificationStore } from '../../utils/NotificationStore';
import NoDataFoundCard from '../noDataFoundCard/NoDataFoundCard';

export enum NotesParent {
  CLIENT,
  INVOICE,
  PLACEMENT,
  CLIENTCONTACT,
  USERPROFILE,
  NOTELISTING,
}

interface INotes {
  parentId: string;
  parent: NotesParent;
  isClient?: boolean;
  clientContactId?: string;
  clientContactName?: string;
  email?: string;
}

interface NotesHandle {
  close: () => Promise<void>;
  open: () => void;
}

const Notes = forwardRef<NotesHandle, INotes>(
  ({ parentId, parent, isClient = false, clientContactId, email }, ref) => {
    const noteFormRef = useRef<HTMLDivElement>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [toggleParent, setToggleParent] = useState<string>('ACTIVE');
    const [notes, setNotes] = useState<NoteDTO[]>([]);
    const setNotification = useNotificationStore((state) => state.setNotification);
    const [openDialog, closeDialog] = useDialog();
    const [loading, setLoading] = useState<boolean>(false);
    const methods = useForm<NoteDTO>();

    useImperativeHandle(ref, () => ({
      close: () =>
        new Promise<void>((resolve) => {
          if (isEditing) {
            openDialog({
              children: (
                <ConfirmationDialog
                  prompt="Do you want to save the note before you leave?"
                  confirmText="Save note"
                  skipText="Skip"
                  onConfirm={async () => {
                    await saveNote(methods.getValues());
                    closeDialog();
                    resolve();
                  }}
                  onSkip={() => {
                    methods.reset();
                    closeDialog();
                    resolve();
                  }}
                />
              ),
            });
          } else {
            resolve();
          }
        }),
      open: () => {},
    }));

    const fetchNotes = useCallback(async () => {
      setLoading(true);
      let response;
      switch (parent) {
        case NotesParent.INVOICE:
          response = await getAllNotesForInvoice(parentId);
          break;
        case NotesParent.PLACEMENT:
          response = await getAllNotesForPlacement(parentId);
          break;
        case NotesParent.CLIENTCONTACT:
          response = await getNotesByClientIdAndClientContactId(parentId, clientContactId ?? '');
          break;
        case NotesParent.USERPROFILE:
          response = await getAllRecruiterNotes(email ?? '');
          break;
        default:
          response = await getAllNotes(parentId);
          break;
      }
      if (response) {
        let filteredNotes = response.data;
        filteredNotes = filteredNotes.filter((note: NoteDTO) =>
          toggleParent === 'ACTIVE'
            ? !note.markArchived
            : toggleParent === 'ARCHIVE'
            ? note.markArchived
            : true
        );
        const finalNotes = isClient
          ? filteredNotes
          : filteredNotes.filter((note: NoteDTO) => !note.markClientNotes);

        setNotes(finalNotes);
        setLoading(false);
      }
    }, [clientContactId, email, isClient, parent, parentId, toggleParent]);

    useEffect(() => {
      fetchNotes();
    }, [fetchNotes]);

    const saveNote = async (data: NoteDTO) => {
      try {
        if (parent === NotesParent.INVOICE) {
          data.id
            ? await updateInvoiceNote(data)
            : await createInvoiceNote({ ...data, invoiceId: parentId } as NoteDTO);
          setNotification(success(`Note has been updated successfully !!`));
        } else if (parent === NotesParent.PLACEMENT) {
          data.id
            ? await updatePlacementNote(data)
            : await createPlacementNote({ ...data, closureId: parentId } as NoteDTO);
          setNotification(success(`Note has been updated successfully !!`));
        } else if (parent === NotesParent.CLIENTCONTACT) {
          data.id
            ? await updateNote(data)
            : await createNote({
                ...data,
                client: { id: parentId },
                markClientNotes: isClient,
                clientContact: { id: clientContactId },
              } as NoteDTO);
          setNotification(success(`Note has been updated successfully !!`));
        } else {
          data.id
            ? await updateNote(data)
            : await createNote({
                ...data,
                client: { id: parentId },
                markClientNotes: isClient,
              } as NoteDTO);
          setNotification(success(`Note has been updated successfully !!`));
        }
        setIsEditing(false);
        methods.reset();
      } catch (e) {
        setNotification(failed('Something went wrong !!'));
      }
    };

    const scrollToNoteForm = () => {
      setTimeout(() => {
        noteFormRef.current &&
          noteFormRef.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }, 50);
    };

    const startEditing = (note: NoteDTO) => {
      methods.reset(JSON.parse(JSON.stringify(note)));
      setIsEditing(true);
      scrollToNoteForm();
    };

    const handleToggleChange = (value) => {
      const newValue = value === 'ACTIVE' ? 'ACTIVE' : 'ARCHIVE';
      setToggleParent(newValue);
    };

    const handleArchiveNote = async (id: string) => {
      try {
        if (parent === NotesParent.INVOICE) {
          await archiveInvoiceNote(id);
          setNotification(success(`Note has been archive successfully !!`));
        } else if (parent === NotesParent.PLACEMENT) {
          await archivePlacementNote(id);
          setNotification(success(`Note has been archive successfully !!`));
        } else {
          await archiveNote(id);
          setNotification(success(`Note has been archive successfully !!`));
        }
      } catch (e) {
        setNotification(failed('Something went wrong !!'));
      }
    };

    const handleUnarchiveNote = async (id: string) => {
      try {
        await unarchiveNote(id);
        setNotification(success(`Note has been unarchive successfully !!`));
      } catch (e) {
        setNotification(failed('Something went wrong !!'));
      }
    };

    const handleSaveNote = async (data: { [key: string]: unknown }) => {
      await saveNote(data as unknown as NoteDTO);
      await fetchNotes();
    };

    const handleCancelNote = () => {
      setIsEditing(false);
      methods.reset();
    };

    const handleClickAddNote = () => {
      methods.reset({});
      setIsEditing(true);
    };

    const handleClickEditNote = (note: NoteDTO) => {
      if (isEditing) {
        const dirtyNote = methods.getValues();
        if (dirtyNote.id === note.id) {
          scrollToNoteForm();
        } else {
          openDialog({
            children: (
              <ConfirmationDialog
                prompt="Do you want to save the previously edited note?"
                confirmText="Save note"
                skipText="Skip"
                onConfirm={async () => {
                  await saveNote(methods.getValues());
                  closeDialog();
                  startEditing(note);
                  await fetchNotes();
                }}
                onSkip={() => {
                  methods.reset();
                  closeDialog();
                  startEditing(note);
                }}
              />
            ),
          });
        }
      } else {
        startEditing(note);
      }
    };

    const handleClickArchiveNote = (note: NoteDTO) => {
      openDialog({
        children: (
          <ConfirmationDialog
            prompt="Are you sure you want to Archive the note?"
            confirmText="Archive note"
            skipText="Cancel"
            onConfirm={async () => {
              closeDialog();
              await handleArchiveNote(note.id);
              await fetchNotes();
            }}
            onSkip={closeDialog}
          />
        ),
      });
    };

    const handleClickUnarchiveNote = (note: NoteDTO) => {
      openDialog({
        children: (
          <ConfirmationDialog
            prompt="Are you sure you want to Unarchive the note?"
            confirmText="Unarchive note"
            skipText="Cancel"
            onConfirm={async () => {
              closeDialog();
              await handleUnarchiveNote(note.id);
              await fetchNotes();
            }}
            onSkip={closeDialog}
          />
        ),
      });
    };

    return (
      <Grid spacing={2} mx={2} pb={12}>
        <Box ref={noteFormRef} paddingTop={2}>
          {isEditing ? (
            <FormProvider {...methods}>
              <AddEditNote onSubmit={handleSaveNote} onCancel={handleCancelNote} />
            </FormProvider>
          ) : (
            <Grid container direction="row" spacing={2} justifyContent="space-between" mb={2}>
              <Grid item>
                <WsToggleButton
                  buttonGroupPadding={6}
                  buttonsList={[
                    { label: 'Active', value: 'ACTIVE' },
                    { label: 'Archived', value: 'ARCHIVE' },
                  ]}
                  defaultValue={toggleParent ?? 'ACTIVE'}
                  selectedIndexValue={handleToggleChange}
                />
              </Grid>
              <Grid item>
                {parent != NotesParent.USERPROFILE && toggleParent == 'ACTIVE' && (
                  <WsButton
                    size="medium"
                    onClick={handleClickAddNote}
                    startIcon={<Add fontSize="small" />}
                  >
                    Add Note
                  </WsButton>
                )}
              </Grid>
            </Grid>
          )}
        </Box>
        {notes && notes.length > 0
          ? notes.map((note) => (
              <Note
                key={note.id}
                note={note}
                onEdit={() => handleClickEditNote(note)}
                onArchive={() => handleClickArchiveNote(note)}
                onUnarchive={() => handleClickUnarchiveNote(note)}
                isClient={isClient}
                parent={parent}
                parentId={parentId}
              />
            ))
          : !isEditing &&
            (loading ? (
              <WsListingLoader />
            ) : toggleParent === 'ACTIVE' ? (
              <NoDataFoundCard
                text="No notes are available. Would you like to add a new Note?"
                icon={<EmptyTeamsAndUsersIcon />}
              />
            ) : (
              <NoDataFoundCard
                text="No archived notes are available"
                icon={<EmptyTeamsAndUsersIcon />}
              />
            ))}
      </Grid>
    );
  }
);

Notes.displayName = 'Notes';

export default Notes;
